// Gabruz Universe — fetch-based API client.
// Talks to the Express/SQL backend in /server.
// Auth: JWT stored in localStorage, sent as Authorization: Bearer <token>.

const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:5000/api/v1";

const TOKEN_KEY = "gabruz-token";
const USER_KEY  = "gabruz-user";

// ============= Token + user helpers =============
export function getToken() { return localStorage.getItem(TOKEN_KEY); }
export function setToken(t) { localStorage.setItem(TOKEN_KEY, t); }
export function clearToken() { localStorage.removeItem(TOKEN_KEY); }

export function getCurrentUser() {
  const raw = localStorage.getItem(USER_KEY);
  return raw ? JSON.parse(raw) : null;
}
export function setCurrentUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}
export function clearCurrentUser() {
  localStorage.removeItem(USER_KEY);
}

export function getUser() {
  return getCurrentUser();
}
export function saveState(state) {
  // No persistent client-side state save needed for this demo.
  return state;
}

// ============= Low-level fetch wrapper =============
// §31 — Errors thrown from here are surfaced to users as toasts, so the
// message must read like English, not like a stack trace. Three sources
// of failure get translated:
//   - network outage / API down  → "Couldn't reach the server…"
//   - 5xx                        → "Server hit an unexpected error…"
//   - everything else            → server's own data.error (already friendly)
async function request(method, path, body) {
  const headers = { "Content-Type": "application/json" };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body)
    });
  } catch (netErr) {
    const err = new Error("Couldn't reach the server. Check your connection and try again.");
    err.status = 0;
    err.cause  = netErr;
    throw err;
  }

  // Auto-logout on auth failure so the UI can redirect to Login
  if (res.status === 401) {
    clearToken();
    clearCurrentUser();
  }

  const text = await res.text();
  const data = text ? safeJson(text) : null;

  if (!res.ok) {
    const serverMsg = data?.error;
    const msg = serverMsg
      || (res.status >= 500
        ? "The server hit an unexpected error. Please try again in a moment."
        : res.statusText || `Request failed (${res.status})`);
    const err = new Error(msg);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

function safeJson(s) { try { return JSON.parse(s); } catch { return s; } }

const get  = (p)         => request("GET",    p);
const post = (p, b)      => request("POST",   p, b);
const put  = (p, b)      => request("PUT",    p, b);
const del  = (p)         => request("DELETE", p);

// ============= Auth =============
export async function login(email, password) {
  const { token, user } = await post("/auth/login", { email, password });
  setToken(token);
  setCurrentUser(user);
  return { user, token };
}

// Public — used by the login screen quick-pick buttons.
export async function listDemoUsers() {
  return get("/auth/demo-users");
}

export function logout() {
  clearToken();
  clearCurrentUser();
}

// PUT /auth/profile — self-service profile update.
// Body: { firstName?, lastName?, displayName?, phone?, email?,
//         oldPassword?, newPassword? }
// If the email changes the server returns a fresh JWT we rotate into
// local storage so subsequent requests don't 401 on stale claims.
export async function updateMyProfile(body) {
  const { user, token } = await put("/auth/profile", body);
  if (token) setToken(token);
  if (user)  setCurrentUser(user);
  return user;
}

// ============= Aggregate load =============
// Fetches everything the views need in parallel and shapes it into the
// flat object the existing components consume: { members, groups, teams,
// matches, availability, squads, announcements, transactions, notifications }.
// Endpoints the user lacks permission for (e.g. transactions for a PLAYER)
// are tolerated — they return [] instead of throwing.
export async function loadState() {
  async function tolerant(path, fallback = []) {
    try { return await get(path); }
    catch (e) { if (e.status === 403) return fallback; throw e; }
  }

  const [
    members, groups, teams, matches, availability,
    squads, announcements, transactions, notifications,
    flyerTemplates
  ] = await Promise.all([
    tolerant("/members"),
    tolerant("/groups"),
    tolerant("/teams"),
    tolerant("/matches"),
    tolerant("/availability"),
    tolerant("/squads"),
    tolerant("/announcements"),
    tolerant("/transactions"),
    tolerant("/notifications"),
    tolerant("/flyer-templates")
  ]);

  return {
    members, groups, teams, matches, availability,
    squads, announcements, transactions, notifications,
    flyerTemplates
  };
}

// Kept as a backwards-compatible no-op so any leftover import works.
export function resetState() {
  // Backend has no reset endpoint by design (use `npm run db:reset` on the server).
  return loadState();
}

// ============= Per-resource API =============
// Multipart upload helper for the Excel schedule preview.
export async function previewSchedule(file) {
  const token = getToken();
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_BASE}/matches/upload`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form
  });
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const err = new Error(data?.error || res.statusText);
    err.status = res.status;
    throw err;
  }
  return data;
}

export async function applySchedule(token, mode) {
  return request("POST", `/matches/upload/apply`, { token, mode });
}

// Authenticated file download — the endpoint requires a Bearer token, so a
// plain <a href> won't work. Fetch + Blob + synthetic <a download> click.
export async function downloadScheduleTemplate() {
  const t = getToken();
  const res = await fetch(`${API_BASE}/matches/upload/template`, {
    headers: t ? { Authorization: `Bearer ${t}` } : {}
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = "schedule-template.xlsx";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export const api = {
  // members
  listMembers:   ()        => get("/members"),
  createMember:  (m)       => post("/members", m),
  updateMember:  (id, m)   => put(`/members/${id}`, m),
  deleteMember:  (id)      => del(`/members/${id}`),

  // groups
  listGroups:    ()        => get("/groups"),
  createGroup:   (g)       => post("/groups", g),
  updateGroup:   (id, g)   => put(`/groups/${id}`, g),
  deleteGroup:   (id)      => del(`/groups/${id}`),
  listGroupMembers:  (id)              => get(`/groups/${id}/members`),
  addGroupMember:    (id, body)        => post(`/groups/${id}/members`, body),
  removeGroupMember: (id, userId)      => del(`/groups/${id}/members/${userId}`),
  listGroupMessages: (id)              => get(`/groups/${id}/messages`),
  postGroupMessage:  (id, body)        => post(`/groups/${id}/messages`, { body }),

  // teams
  listTeams:     ()        => get("/teams"),
  createTeam:    (t)       => post("/teams", t),
  updateTeam:    (id, t)   => put(`/teams/${id}`, t),
  deleteTeam:    (id)      => del(`/teams/${id}`),

  // matches
  listMatches:   ()        => get("/matches"),
  createMatch:   (m)       => post("/matches", m),
  updateMatch:   (id, m)   => put(`/matches/${id}`, m),
  deleteMatch:   (id)      => del(`/matches/${id}`),
  previewSchedule,
  applySchedule,
  downloadScheduleTemplate,

  // availability
  listAvailability: ()                   => get("/availability"),
  forMatch:         (matchId)            => get(`/availability/${matchId}`),
  setAvailability:  (matchId, userId, body) => put(`/availability/${matchId}/${userId}`, body),

  // squads
  listSquads:    ()        => get("/squads"),
  createSquad:   (s)       => post("/squads", s),
  updateSquad:   (id, s)   => put(`/squads/${id}`, s),
  deleteSquad:   (id)      => del(`/squads/${id}`),

  // announcements
  listAnnouncements: ()         => get("/announcements"),
  createAnnouncement: (a)       => post("/announcements", a),
  updateAnnouncement: (id, a)   => put(`/announcements/${id}`, a),
  deleteAnnouncement: (id)      => del(`/announcements/${id}`),

  // transactions
  listTransactions:  (teamId)   => get(`/transactions${teamId ? `?teamId=${teamId}` : ""}`),
  listTeamFinanceSummary: ()    => get("/transactions/by-team"),
  createTransaction: (t)        => post("/transactions", t),
  deleteTransaction: (id)       => del(`/transactions/${id}`),
  financeSummary:    ()         => get("/transactions/summary"),

  // team fees — master config + per-player collection
  getTeamFeeConfig:    (teamId, year) => get(`/team-fees/config?teamId=${teamId}&year=${year}`),
  saveTeamFeeConfig:   (cfg)          => put("/team-fees/config", cfg),
  listPlayerFees:      (teamId, year) => get(`/team-fees/players?teamId=${teamId}&year=${year}`),
  savePlayerFee:       (row)          => put("/team-fees/players", row),

  // direct messages — cross-team chat
  listMessageThreads:  ()             => get("/messages/threads"),
  getMessagesWith:     (userId)       => get(`/messages/with/${userId}`),
  unreadMessageCount:  ()             => get("/messages/unread"),
  sendMessage:         (toUserId, body) => post("/messages", { toUserId, body }),
  // group chat rooms
  createChatRoom:      (name, memberIds) => post("/messages/rooms", { name, memberIds }),
  getChatRoom:         (roomId)       => get(`/messages/rooms/${roomId}`),
  sendRoomMessage:     (roomId, body) => post(`/messages/rooms/${roomId}/messages`, { body }),

  // notifications
  listNotifications: ()         => get("/notifications"),
  createNotification:(n)        => post("/notifications", n),
  markRead:          (id)       => put(`/notifications/${id}/read`),

  // practice & development (Phase 3 — Rowdys Cricket scoped in the UI)
  listPracticeSessions:   (teamId) => get(`/practice/sessions${teamId ? `?teamId=${teamId}` : ""}`),
  createPracticeSession:  (body)   => post("/practice/sessions", body),
  updatePracticeSession:  (id, b)  => put(`/practice/sessions/${id}`, b),
  deletePracticeSession:  (id)     => del(`/practice/sessions/${id}`),

  listPracticeAttendance: (teamId) => get(`/practice/attendance${teamId ? `?teamId=${teamId}` : ""}`),
  setPracticeAttendance:  (sessionId, userId, body) => put(`/practice/attendance/${sessionId}/${userId}`, body),

  listDrills:    (teamId) => get(`/practice/drills${teamId ? `?teamId=${teamId}` : ""}`),
  createDrill:   (body)   => post("/practice/drills", body),
  updateDrill:   (id, b)  => put(`/practice/drills/${id}`, b),
  deleteDrill:   (id)     => del(`/practice/drills/${id}`),

  listGoals:     (teamId) => get(`/practice/goals${teamId ? `?teamId=${teamId}` : ""}`),
  createGoal:    (body)   => post("/practice/goals", body),
  updateGoal:    (id, b)  => put(`/practice/goals/${id}`, b),
  deleteGoal:    (id)     => del(`/practice/goals/${id}`),

  listSkillRatings:   (teamId) => get(`/practice/skill-ratings${teamId ? `?teamId=${teamId}` : ""}`),
  createSkillRating:  (body)   => post("/practice/skill-ratings", body),
  deleteSkillRating:  (id)     => del(`/practice/skill-ratings/${id}`),

  // Phase 3 — cricket player career stats (per team, per player).
  listPlayerStats: (teamId)             => get(`/player-stats?teamId=${teamId}`),
  getPlayerStats:  (userId, teamId)     => get(`/player-stats/${userId}?teamId=${teamId}`),
  savePlayerStats: (userId, body)       => put(`/player-stats/${userId}`, body),

  // Phase 3 — admin-managed custom cricket stat fields.
  listCricketStatFields:  ()       => get("/cricket-stat-fields"),
  createCricketStatField: (body)   => post("/cricket-stat-fields", body),
  updateCricketStatField: (id, b)  => put(`/cricket-stat-fields/${id}`, b),
  deleteCricketStatField: (id)     => del(`/cricket-stat-fields/${id}`),

  // Phase 3 — per-team flyer template designer.
  getFlyerTemplate:  (teamId)       => get(`/flyer-templates/${teamId}`),
  saveFlyerTemplate: (teamId, body) => put(`/flyer-templates/${teamId}`, body),

  // Phase 3 — per-team uploaded logo (base64 data URL).
  uploadTeamLogo: (teamId, dataUrl) => put(`/teams/${teamId}/logo`, { logoUrl: dataUrl }),

  // Phase 4 — per-match cricket performance entries that auto-roll into
  // career stats. See server/src/routes/matchPerformance.js.
  listMatchPerformance: (matchId)         => get(`/match-performance?matchId=${matchId}`),
  getUserMatchHistory:  (userId)          => get(`/match-performance/user/${userId}`),
  getUserMatchTotals:   (userId)          => get(`/match-performance/totals/${userId}`),
  saveMatchPerformance: (matchId, rows)   => put("/match-performance/bulk", { matchId, rows }),
  deleteMatchPerformance: (perfId)        => del(`/match-performance/${perfId}`)
};
